import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Icon } from "@/components/Icon";

/**
 * PWA installability chrome: the "new version available" toast and the
 * one-off "app ready offline" notice.
 *
 * `useRegisterSW` comes from `vite-plugin-pwa` (registered with
 * `registerType: 'prompt'`, see `vite.config.ts`). The prompt variant
 * means the service worker never swaps itself silently: we surface a
 * toast and only call `updateServiceWorker(true)` when the user accepts
 * (`skipWaiting()` + reload). In dev (and under vitest) the virtual
 * module resolves to a no-op, so this renders nothing.
 *
 * Styling matches the app's existing toast language (clay-900 bar,
 * pill buttons) from `ScheduleScreen`.
 */
export function PwaUpdatePrompt() {
  const { t } = useTranslation();
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  if (!offlineReady && !needRefresh) return null;

  const dismiss = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
  };

  const applyUpdate = () => {
    // `true` → skipWaiting() then reload, so the new app shell
    // takes over immediately instead of on the next navigation.
    updateServiceWorker(true);
  };

  return (
    <div
      role="status"
      data-testid="pwa-update-toast"
      style={{
        position: "fixed",
        left: 20,
        right: 20,
        bottom: 92,
        zIndex: 70,
        background: "var(--clay-900)",
        color: "#fff",
        borderRadius: "var(--radius-md)",
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        boxShadow: "var(--shadow-lg)",
        font: "var(--fw-medium) var(--fs-sm)/1.3 var(--font-body)",
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 999,
          background: "var(--sage-500)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
          color: "#fff",
        }}
      >
        <Icon name="check" size={16} color="#fff" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        {needRefresh
          ? t("pwa.newVersion")
          : offlineReady
            ? t("pwa.readyOffline")
            : ""}
      </span>
      <span style={{ display: "flex", gap: 8, flex: "none" }}>
        {needRefresh ? (
          <>
            <button
              type="button"
              onClick={applyUpdate}
              data-testid="pwa-update-apply"
              style={{
                height: 32,
                padding: "0 14px",
                borderRadius: "var(--radius-pill)",
                border: "none",
                background: "var(--brand)",
                color: "#fff",
                font: "var(--fw-semibold) var(--fs-xs)/1 var(--font-body)",
                cursor: "pointer",
              }}
            >
              {t("pwa.update")}
            </button>
            <button
              type="button"
              onClick={dismiss}
              aria-label={t("common.cancel")}
              data-testid="pwa-update-dismiss"
              style={{
                height: 32,
                padding: "0 10px",
                borderRadius: "var(--radius-pill)",
                border: "1px solid rgba(255,255,255,0.25)",
                background: "transparent",
                color: "#fff",
                font: "var(--fw-semibold) var(--fs-xs)/1 var(--font-body)",
                cursor: "pointer",
              }}
            >
              {t("common.cancel")}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={dismiss}
            data-testid="pwa-offline-dismiss"
            style={{
              height: 32,
              padding: "0 14px",
              borderRadius: "var(--radius-pill)",
              border: "none",
              background: "var(--brand)",
              color: "#fff",
              font: "var(--fw-semibold) var(--fs-xs)/1 var(--font-body)",
              cursor: "pointer",
            }}
          >
            {t("common.gotIt")}
          </button>
        )}
      </span>
    </div>
  );
}
